import * as React from "react";
import { useNavigate } from "react-router-dom";
import { listCustomers, listEsims, listOrders } from "@/api/admin";
import { customerPath, esimPath, orderPath } from "@/components/EntityLink";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { NAV_GROUPS } from "@/app/nav";

/**
 * Ctrl+K: the panel's pages, and the orders, eSIMs and customers a typed
 * text finds.
 *
 * The records come from the three lists' own endpoints, so each is found by
 * that list's rule and no other: an order by its code, an eSIM by its ICCID,
 * number, address or code, a customer by any part of the address. A result
 * opens the record itself, not a list filtered to it.
 */
interface Hit {
  id: string;
  label: string;
  /** What the hit is, said beside it. */
  hint: string;
  to: string;
}

/** Ctrl+K, or Cmd+K on a Mac, from anywhere in the panel. */
export const isSearchShortcut = (e: Pick<KeyboardEvent, "ctrlKey" | "metaKey" | "key">) =>
  (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";

/** A shorter text finds too much to be worth asking about. */
const MIN_QUERY = 2;
/** Per list: a palette points somewhere, it does not replace the list. */
const MAX_RECORDS = "5";
/** Long enough that a typist's next letter cancels the last question. */
const DEBOUNCE_MS = 250;

const PAGES: Hit[] = NAV_GROUPS.flatMap((group) =>
  group.items.map((item) => ({
    id: `page:${item.to}`,
    label: item.label,
    hint: "Page",
    to: item.to,
  })),
);

/** The records a text finds; a list that fails answers none. */
async function findRecords(q: string): Promise<Hit[]> {
  const [orders, esims, customers] = await Promise.all([
    listOrders({ code: q, limit: MAX_RECORDS }).catch(() => null),
    listEsims({ q, limit: MAX_RECORDS }).catch(() => null),
    listCustomers({ q, limit: MAX_RECORDS }).catch(() => null),
  ]);
  return [
    ...(orders?.rows ?? []).map((row) => ({
      id: `order:${row.order_code}`,
      label: row.order_code,
      hint: "Order",
      to: orderPath(row.order_code),
    })),
    ...(esims?.rows ?? []).map((row) => ({
      id: `esim:${row.iccid}`,
      label: row.msisdn ? `${row.iccid} · ${row.msisdn}` : row.iccid,
      hint: "eSIM",
      to: esimPath(row.iccid),
    })),
    ...(customers?.rows ?? []).map((row) => ({
      id: `customer:${row.email}`,
      label: row.email,
      hint: "Customer",
      to: customerPath(row.email),
    })),
  ];
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const input = React.useRef<HTMLInputElement>(null);
  const [text, setText] = React.useState("");
  // Keyed to the text they answer, so an answer to an older text is never
  // shown as this one's, and "nothing" waits for the answer.
  const [found, setFound] = React.useState<{ q: string; hits: Hit[] } | null>(null);
  const [active, setActive] = React.useState(0);

  // Each opening starts clean: last time's results answer nobody's question.
  React.useEffect(() => {
    if (!open) return;
    setText("");
    setFound(null);
    setActive(0);
  }, [open]);

  const q = text.trim();
  const asking = open && q.length >= MIN_QUERY;
  React.useEffect(() => {
    if (!asking) return;
    let alive = true;
    const timer = setTimeout(async () => {
      const hits = await findRecords(q);
      if (alive) setFound({ q, hits });
    }, DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [asking, q]);

  const needle = q.toLowerCase();
  const pages = PAGES.filter((page) => page.label.toLowerCase().includes(needle));
  const records = asking && found?.q === q ? found.hits : [];
  const searching = asking && found?.q !== q;
  const hits = [...pages, ...records];
  // The list shrank under the cursor: Enter must not point at nothing.
  const current = Math.min(active, Math.max(hits.length - 1, 0));

  function go(hit: Hit | undefined) {
    if (!hit) return;
    onClose();
    navigate(hit.to);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (hits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((current + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((current - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(hits[current]);
    }
  }

  return (
    <Modal open={open} title="Go to" onClose={onClose} initialFocus={input}>
      <div className="space-y-3">
        <Input
          ref={input}
          role="combobox"
          aria-expanded={hits.length > 0}
          aria-controls="palette-hits"
          aria-activedescendant={hits[current] ? `palette-${hits[current].id}` : undefined}
          aria-label="Search pages, orders, eSIMs and customers"
          placeholder="Page, order code, ICCID, number or address"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        <ul id="palette-hits" role="listbox" className="space-y-0.5">
          {hits.map((hit, index) => (
            <li
              key={hit.id}
              id={`palette-${hit.id}`}
              role="option"
              aria-selected={index === current}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-3 rounded px-3 py-2 text-sm",
                index === current ? "bg-hover" : "hover:bg-hover",
              )}
              onMouseEnter={() => setActive(index)}
              onClick={() => go(hit)}
            >
              <span className="min-w-0 truncate">{hit.label}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{hit.hint}</span>
            </li>
          ))}
          {hits.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {searching ? "Searching…" : "Nothing matches."}
            </li>
          )}
        </ul>
      </div>
    </Modal>
  );
}
